// react-magnetic-di - temporary comment, for auto dependency injection in tests
import { useCallback, useMemo, useState } from 'react';

import { useIntl } from 'react-intl-next';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { parseExtensionId } from '@atlassian/forge-ui/utils/parseExtensionId';
import { fireOperationalEvent } from '@atlassian/ui-modifications-analytics';

import type { ForgeExtension } from '../../../../../../common/types/apps';
import { useAdjustmentsContextActions } from '../../../../../../controllers/adjustments-context';
import { useUIModificationsConsentStatusForApp } from '../../../../../../controllers/adjustments-context/hooks/use-consent-status';
import {
	UIM_CONSENT_CONSENTED,
	UIM_CONSENT_REQUIRED,
	UIM_CONSENT_UNKNOWN,
} from '../../../../../../controllers/adjustments-context/types';
import { useViewType } from '../../../../../../controllers/issue-adjustments/hooks/use-view-type';
import { getConsentMessage } from '../../../../../../controllers/view-configuration';

type Props = {
	forgeExtension: ForgeExtension;
};

type ThreeLOPromptComponentProps = {
	onConsentSuccess: () => void;
	onConsentInitialization: () => void;
	consentMessage: string;
};

export const useThreeLOPromptProps = ({ forgeExtension }: Props): ThreeLOPromptComponentProps => {
	const [hasBeenTracked, setHasBeenTracked] = useState<boolean>(false);
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { setUIModificationsConsentStatus } = useAdjustmentsContextActions();
	const { appId } = parseExtensionId(forgeExtension.id);
	const viewType = useViewType();

	const { formatMessage } = useIntl();

	const currentUIModificationsConsentStatus = useUIModificationsConsentStatusForApp({ appId });
	const onConsentSuccess = useCallback(() => {
		setUIModificationsConsentStatus({ appId, status: UIM_CONSENT_CONSENTED });

		fireOperationalEvent(createAnalyticsEvent, {
			action: 'cleared',
			actionSubject: 'jiraUiModificationsObligatoryConsent',
		});
	}, [appId, createAnalyticsEvent, setUIModificationsConsentStatus]);

	const onConsentInitialization = useCallback(() => {
		if (currentUIModificationsConsentStatus === UIM_CONSENT_UNKNOWN) {
			setUIModificationsConsentStatus({ appId, status: UIM_CONSENT_REQUIRED });

			if (!hasBeenTracked) {
				fireOperationalEvent(createAnalyticsEvent, {
					action: 'triggered',
					actionSubject: 'jiraUiModifications.obligatoryConsent',
					attributes: {
						viewType,
					},
				});

				setHasBeenTracked(true);
			}
		}
	}, [
		appId,
		currentUIModificationsConsentStatus,
		setUIModificationsConsentStatus,
		hasBeenTracked,
		viewType,
		createAnalyticsEvent,
	]);

	const consentMessage = useMemo(() => {
		return formatMessage(getConsentMessage(viewType), {
			appTitle: forgeExtension.properties.title,
		});
	}, [formatMessage, forgeExtension.properties.title, viewType]);

	const threeLOPromptComponentPropsMethods = useMemo(
		() => ({ onConsentSuccess, onConsentInitialization, consentMessage }),
		[onConsentSuccess, onConsentInitialization, consentMessage],
	);

	return threeLOPromptComponentPropsMethods;
};
